import React, { FC, useEffect } from 'react';
import { CssBaseline } from '@material-ui/core';
import { StylesProvider, MuiThemeProvider, jssPreset } from '@material-ui/core/styles';
import { create } from 'jss';
import jssPluginIsolate from 'jss-plugin-isolate';
import Auth from '@aws-amplify/auth';

import { I18nextProvider } from 'react-i18next';
import { Router, withRouter, RouteComponentProps } from 'react-router-dom';
import { createBrowserHistory } from 'history';

import { AuthProvider, NotifyProvider } from 'providers';
import { Loaders, ScrollToTop } from 'components';
import Cookies from 'cookies';

import i18n from 'config/i18n';

import theme from 'theme';
import { getSubdomainUrl, isSignedIn, isSignedOut } from 'helpers';
import { isRoot } from 'utilities';
import { useAuth } from 'hooks';
import * as paths from 'paths';

import { ApolloProtected, ApolloPublic } from './providers';
import Protected from './protected/ProtectedIndex';
import Public from './public/PublicIndex';

import Connections from './protected/Connections';
import Routes from './protected/Routes';

const jss = create({
  plugins: [...jssPreset().plugins, jssPluginIsolate()],
});

const ValidateUser: FC<RouteComponentProps> = ({ history }) => {
  const { data } = useAuth();
  const signedIn = isSignedIn(data.state);
  const signedOut = isSignedOut(data.state);
  const isUnsubscribing = window.location.pathname.includes(paths.unsubscribe);
  const params = new URLSearchParams(window.location.search);

  // Signed in and visiting base domain
  if (signedIn && isRoot()) {
    if (isUnsubscribing) {
      return renderPublic();
    }

    if (Cookies.clientSubdomain) {
      window.location.assign(getSubdomainUrl(Cookies.clientSubdomain));
      return null;
    }
  }

  // Direct SSO login from base domain
  if (signedOut && isRoot() && params.get('provider')) {
    Auth.federatedSignIn({ customProvider: params.get('provider') });
    return null;
  }

  useEffect(() => {
    analytics.page();
    history.listen(() => analytics.page());
  }, []);

  function renderProtected() {
    return (
      <ApolloProtected>
        <NotifyProvider>
          <Protected>
            <Connections />
            <Routes />
          </Protected>
        </NotifyProvider>
      </ApolloProtected>
    );
  }

  function renderPublic() {
    return (
      <ApolloPublic>
        <NotifyProvider>
          <Public />
        </NotifyProvider>
      </ApolloPublic>
    );
  }

  if (signedIn) {
    return renderProtected();
  }

  if (signedOut) {
    return renderPublic();
  }

  return <Loaders.StandardNoHeader />;
};

const EnhancedUserValidator = withRouter(ValidateUser);

const App: FC = () => (
  <StylesProvider jss={jss}>
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <I18nextProvider i18n={i18n}>
        <Router history={createBrowserHistory()}>
          <ScrollToTop />
          <AuthProvider>
            <EnhancedUserValidator />
          </AuthProvider>
        </Router>
      </I18nextProvider>
    </MuiThemeProvider>
  </StylesProvider>
);

export default App;
